import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Utensils, Search, Plus, Trash2, ChevronLeft, ChevronRight, 
  Loader2, PieChart, MessageSquare, Send, X, Coffee, Sun, Moon, Cookie,
  Flame, Beef, Wheat, Droplet
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart as RechartsPie, Pie, Cell, Legend, Tooltip } from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';

import { API } from '../config/api';

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Breakfast', icon: Coffee, color: 'text-orange-400' },
  { value: 'lunch', label: 'Lunch', icon: Sun, color: 'text-yellow-400' },
  { value: 'dinner', label: 'Dinner', icon: Moon, color: 'text-blue-400' },
  { value: 'snack', label: 'Snack', icon: Cookie, color: 'text-pink-400' }
];

const MACRO_COLORS = {
  protein: '#ef4444',
  carbs: '#22c55e', 
  fat: '#eab308'
};

export default function MealTracker({ childId, childName }) {
  const { user } = useAuth();
  const [view, setView] = useState('daily'); // daily, weekly
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  
  // Daily view state
  const [dailyMeals, setDailyMeals] = useState([]);
  const [dailyTotals, setDailyTotals] = useState({});
  
  // Weekly view state
  const [weeklySummary, setWeeklySummary] = useState(null);
  
  // Add meal state
  const [showAddMeal, setShowAddMeal] = useState(false);
  const [mealType, setMealType] = useState('breakfast');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [mealNotes, setMealNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Comment state
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  
  const [loading, setLoading] = useState(true);
  
  const isCoachOrAdmin = user?.role === 'coach' || user?.role === 'admin';

  const loadDailyMeals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/meals/child/${childId}/daily`, {
        params: { date: selectedDate.toISOString() }
      });
      setDailyMeals(response.data.meals || []);
      setDailyTotals(response.data.daily_totals || {});
    } catch (error) {
      console.error('Failed to load meals:', error);
    } finally {
      setLoading(false);
    }
  }, [childId, selectedDate]);

  const loadWeeklySummary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/meals/child/${childId}/weekly`, {
        params: { week_start: weekStart.toISOString() }
      });
      setWeeklySummary(response.data);
    } catch (error) {
      console.error('Failed to load weekly summary:', error);
    } finally {
      setLoading(false);
    }
  }, [childId, weekStart]);

  useEffect(() => {
    if (view === 'daily') {
      loadDailyMeals();
    } else {
      loadWeeklySummary();
    }
  }, [view, loadDailyMeals, loadWeeklySummary]);

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  const searchFoods = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/meals/search`, {
        params: { query: searchQuery, max_results: 15 }
      });
      setSearchResults(response.data.foods || []);
    } catch (error) {
      toast.error('Failed to search foods');
    } finally {
      setSearching(false);
    }
  };

  const addFoodToMeal = (food) => {
    setSelectedFoods([...selectedFoods, {
      ...food,
      servings: 1,
      id: `${food.food_id}-${Date.now()}`
    }]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const updateFoodServings = (foodId, servings) => {
    setSelectedFoods(selectedFoods.map(f => 
      f.id === foodId ? { ...f, servings: parseFloat(servings) || 1 } : f
    ));
  };

  const removeFoodFromMeal = (foodId) => {
    setSelectedFoods(selectedFoods.filter(f => f.id !== foodId));
  };

  const submitMeal = async () => {
    if (selectedFoods.length === 0) {
      toast.error('Please add at least one food item');
      return;
    }

    setSubmitting(true);
    try {
      const mealData = {
        child_id: childId,
        meal_type: mealType,
        date: selectedDate.toISOString(),
        foods: selectedFoods.map(f => ({
          food_id: f.food_id,
          food_name: f.food_name,
          serving_description: f.serving,
          number_of_servings: f.servings,
          calories: f.calories,
          protein: f.protein,
          carbs: f.carbs,
          fat: f.fat
        })),
        notes: mealNotes || null
      };

      await axios.post(`${API}/meals/log`, mealData);
      toast.success('Meal logged successfully!');
      
      // Reset form
      setShowAddMeal(false);
      setSelectedFoods([]);
      setMealNotes('');
      setSearchQuery('');
      setSearchResults([]);
      
      // Reload data
      loadDailyMeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log meal');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteMeal = async (mealId) => {
    if (!window.confirm('Delete this meal?')) return;
    
    try {
      await axios.delete(`${API}/meals/log/${mealId}`);
      toast.success('Meal deleted');
      loadDailyMeals();
    } catch (error) {
      toast.error('Failed to delete meal');
    }
  };

  const addWeeklyComment = async () => {
    if (!newComment.trim()) return;
    
    setAddingComment(true);
    try {
      await axios.post(`${API}/meals/child/${childId}/weekly-comment`, 
        { text: newComment },
        { params: { week_start: weekStart.toISOString() } }
      );
      toast.success('Comment added');
      setNewComment('');
      loadWeeklySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', { 
      weekday: 'short', day: 'numeric', month: 'short' 
    });
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newWeekStart = new Date(weekStart);
    newWeekStart.setDate(newWeekStart.getDate() + (direction * 7));
    setWeekStart(newWeekStart);
  };

  // Calculate totals for selected foods
  const selectedTotals = selectedFoods.reduce((acc, food) => ({
    calories: acc.calories + (food.calories * food.servings),
    protein: acc.protein + (food.protein * food.servings),
    carbs: acc.carbs + (food.carbs * food.servings),
    fat: acc.fat + (food.fat * food.servings)
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  // Prepare pie chart data
  const pieChartData = weeklySummary ? [
    { name: 'Protein', value: weeklySummary.macro_percentages.protein, color: MACRO_COLORS.protein },
    { name: 'Carbs', value: weeklySummary.macro_percentages.carbs, color: MACRO_COLORS.carbs },
    { name: 'Fat', value: weeklySummary.macro_percentages.fat, color: MACRO_COLORS.fat }
  ] : [];

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid="meal-tracker">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Utensils className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="text-lg font-bold text-white">Meal Tracker</h3>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={view === 'daily' ? 'default' : 'outline'}
            onClick={() => setView('daily')}
            className={view === 'daily' ? 'bg-[#02b5ff]' : 'border-zinc-700'}
          >
            Daily
          </Button>
          <Button
            size="sm"
            variant={view === 'weekly' ? 'default' : 'outline'}
            onClick={() => setView('weekly')}
            className={view === 'weekly' ? 'bg-[#02b5ff]' : 'border-zinc-700'}
          >
            Weekly
          </Button>
        </div>
      </div>

      {/* Daily View */}
      {view === 'daily' && (
        <>
          {/* Date Navigation */}
          <div className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3 mb-4">
            <Button size="sm" variant="ghost" onClick={() => navigateDate(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium text-white">{formatDate(selectedDate)}</span>
            <Button size="sm" variant="ghost" onClick={() => navigateDate(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Daily Totals */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="bg-zinc-800/30 rounded-lg p-3 text-center">
              <Flame className="h-4 w-4 text-orange-400 mx-auto mb-1" />
              <div className="text-lg font-bold text-white">{dailyTotals.calories || 0}</div>
              <div className="text-xs text-zinc-500">Calories</div>
            </div>
            <div className="bg-zinc-800/30 rounded-lg p-3 text-center">
              <Beef className="h-4 w-4 text-red-400 mx-auto mb-1" />
              <div className="text-lg font-bold text-white">{dailyTotals.protein || 0}g</div>
              <div className="text-xs text-zinc-500">Protein</div>
            </div>
            <div className="bg-zinc-800/30 rounded-lg p-3 text-center">
              <Wheat className="h-4 w-4 text-green-400 mx-auto mb-1" />
              <div className="text-lg font-bold text-white">{dailyTotals.carbs || 0}g</div>
              <div className="text-xs text-zinc-500">Carbs</div>
            </div>
            <div className="bg-zinc-800/30 rounded-lg p-3 text-center">
              <Droplet className="h-4 w-4 text-yellow-400 mx-auto mb-1" />
              <div className="text-lg font-bold text-white">{dailyTotals.fat || 0}g</div>
              <div className="text-xs text-zinc-500">Fat</div>
            </div>
          </div>

          {/* Add Meal Button */}
          {!showAddMeal && (
            <Button
              onClick={() => setShowAddMeal(true)}
              className="w-full mb-4 bg-[#02b5ff] hover:bg-[#0099dd]"
              data-testid="add-meal-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Log Meal
            </Button>
          )}

          {/* Add Meal Form */}
          {showAddMeal && (
            <div className="bg-zinc-800/50 rounded-lg p-4 mb-4 border border-zinc-700">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-white">Log New Meal</h4>
                <Button size="sm" variant="ghost" onClick={() => {
                  setShowAddMeal(false);
                  setSelectedFoods([]);
                  setSearchQuery('');
                  setSearchResults([]);
                }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Meal Type Selection */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {MEAL_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setMealType(type.value)}
                      className={`p-2 rounded-lg border transition-all ${
                        mealType === type.value 
                          ? 'border-[#02b5ff] bg-[#02b5ff]/10' 
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <Icon className={`h-4 w-4 mx-auto mb-1 ${type.color}`} />
                      <div className="text-xs text-white">{type.label}</div>
                    </button>
                  );
                })}
              </div>

              {/* Food Search */}
              <div className="mb-4">
                <div className="flex gap-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchFoods()}
                    placeholder="Search foods..."
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="food-search-input"
                  />
                  <Button onClick={searchFoods} disabled={searching} className="bg-[#02b5ff]">
                    {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 bg-zinc-900 rounded-lg border border-zinc-700 max-h-48 overflow-y-auto">
                    {searchResults.map(food => (
                      <button
                        key={food.food_id}
                        onClick={() => addFoodToMeal(food)}
                        className="w-full p-3 text-left hover:bg-zinc-800 border-b border-zinc-800 last:border-0"
                      >
                        <div className="font-medium text-white text-sm">{food.food_name}</div>
                        <div className="text-xs text-zinc-500">
                          {food.serving} | {food.calories} cal | P: {food.protein}g | C: {food.carbs}g | F: {food.fat}g
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Foods */}
              {selectedFoods.length > 0 && (
                <div className="mb-4 space-y-2">
                  <div className="text-sm font-medium text-zinc-400">Selected Foods</div>
                  {selectedFoods.map(food => (
                    <div key={food.id} className="flex items-center gap-2 bg-zinc-900/50 rounded p-2">
                      <div className="flex-1">
                        <div className="text-sm text-white">{food.food_name}</div>
                        <div className="text-xs text-zinc-500">
                          {Math.round(food.calories * food.servings)} cal
                        </div>
                      </div>
                      <Input
                        type="number"
                        min="0.25"
                        step="0.25"
                        value={food.servings}
                        onChange={(e) => updateFoodServings(food.id, e.target.value)}
                        className="w-20 h-8 bg-zinc-800 border-zinc-700 text-white text-center"
                      />
                      <span className="text-xs text-zinc-500">servings</span>
                      <Button size="sm" variant="ghost" onClick={() => removeFoodFromMeal(food.id)}>
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  ))}
                  
                  {/* Totals */}
                  <div className="bg-[#02b5ff]/10 rounded p-2 text-sm">
                    <span className="text-zinc-400">Total: </span>
                    <span className="text-white font-medium">
                      {Math.round(selectedTotals.calories)} cal | 
                      P: {Math.round(selectedTotals.protein)}g | 
                      C: {Math.round(selectedTotals.carbs)}g | 
                      F: {Math.round(selectedTotals.fat)}g
                    </span>
                  </div>
                </div>
              )}

              {/* Notes */}
              <Textarea
                value={mealNotes}
                onChange={(e) => setMealNotes(e.target.value)}
                placeholder="Add notes (optional)"
                className="bg-zinc-900 border-zinc-700 text-white mb-4 h-16"
              />

              {/* Submit */}
              <Button
                onClick={submitMeal}
                disabled={submitting || selectedFoods.length === 0}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="submit-meal-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Log Meal
              </Button>
            </div>
          )}

          {/* Meals List */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
            </div>
          ) : dailyMeals.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <Utensils className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No meals logged for this day</p>
            </div>
          ) : (
            <div className="space-y-3">
              {MEAL_TYPES.map(type => {
                const mealsOfType = dailyMeals.filter(m => m.meal_type === type.value);
                if (mealsOfType.length === 0) return null;
                
                const Icon = type.icon;
                return (
                  <div key={type.value}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`h-4 w-4 ${type.color}`} />
                      <span className="text-sm font-medium text-white">{type.label}</span>
                    </div>
                    {mealsOfType.map(meal => (
                      <div key={meal.id} className="bg-zinc-800/30 rounded-lg p-3 mb-2">
                        <div className="flex justify-between items-start mb-2">
                          <div className="text-xs text-zinc-500">
                            {meal.totals.calories} cal | P: {meal.totals.protein}g | C: {meal.totals.carbs}g | F: {meal.totals.fat}g
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => deleteMeal(meal.id)}>
                            <Trash2 className="h-3 w-3 text-zinc-500 hover:text-red-400" />
                          </Button>
                        </div>
                        <div className="space-y-1">
                          {meal.foods.map((food, idx) => (
                            <div key={idx} className="text-sm text-zinc-300">
                              • {food.food_name} ({food.number_of_servings}x)
                            </div>
                          ))}
                        </div>
                        {meal.notes && (
                          <div className="text-xs text-zinc-500 mt-2 italic">{meal.notes}</div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Weekly View */}
      {view === 'weekly' && (
        <>
          {/* Week Navigation */}
          <div className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3 mb-4">
            <Button size="sm" variant="ghost" onClick={() => navigateWeek(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium text-white">
              {formatDate(weekStart)} - {formatDate(new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000))}
            </span>
            <Button size="sm" variant="ghost" onClick={() => navigateWeek(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
            </div>
          ) : weeklySummary ? (
            <>
              {/* Weekly Totals */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-zinc-800/30 rounded-lg p-4">
                  <div className="text-sm text-zinc-500 mb-2">Weekly Total</div>
                  <div className="text-2xl font-bold text-white">{weeklySummary.weekly_totals.calories}</div>
                  <div className="text-xs text-zinc-500">calories</div>
                </div>
                <div className="bg-zinc-800/30 rounded-lg p-4">
                  <div className="text-sm text-zinc-500 mb-2">Daily Average</div>
                  <div className="text-2xl font-bold text-[#02b5ff]">{weeklySummary.daily_average.calories}</div>
                  <div className="text-xs text-zinc-500">calories/day</div>
                </div>
              </div>

              {/* Macro Pie Chart */}
              {pieChartData.some(d => d.value > 0) && (
                <div className="bg-zinc-800/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <PieChart className="h-4 w-4 text-[#02b5ff]" />
                    <span className="text-sm font-medium text-white">Macronutrient Breakdown</span>
                  </div>
                  <div className="h-48 min-h-[192px]">
                    <SafeResponsiveContainer width="100%" height="100%" minHeight={192}>
                      <RechartsPie>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}%`}
                          labelLine={false}
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => `${value}%`}
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                        />
                        <Legend />
                      </RechartsPie>
                    </SafeResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <div className="text-center">
                      <div className="text-sm font-bold text-red-400">{weeklySummary.weekly_totals.protein}g</div>
                      <div className="text-xs text-zinc-500">Protein</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-green-400">{weeklySummary.weekly_totals.carbs}g</div>
                      <div className="text-xs text-zinc-500">Carbs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-yellow-400">{weeklySummary.weekly_totals.fat}g</div>
                      <div className="text-xs text-zinc-500">Fat</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Daily Breakdown */}
              <div className="bg-zinc-800/30 rounded-lg p-4 mb-4">
                <div className="text-sm font-medium text-white mb-3">Daily Breakdown</div>
                <div className="space-y-2">
                  {weeklySummary.daily_data.map(day => (
                    <div key={day.date} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 w-20">{day.day_name.slice(0, 3)}</span>
                      <div className="flex-1 mx-2 bg-zinc-700 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-[#02b5ff] h-full transition-all"
                          style={{ 
                            width: `${Math.min((day.calories / (weeklySummary.daily_average.calories * 1.5)) * 100, 100)}%` 
                          }}
                        />
                      </div>
                      <span className="text-white w-16 text-right">{day.calories} cal</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coach Comments Section */}
              <div className="bg-zinc-800/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="h-4 w-4 text-[#02b5ff]" />
                  <span className="text-sm font-medium text-white">Coach Comments</span>
                </div>

                {/* Existing Comments */}
                {weeklySummary.comments && weeklySummary.comments.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {weeklySummary.comments.map(comment => (
                      <div key={comment.id} className="bg-zinc-900/50 rounded p-3 border-l-2 border-[#02b5ff]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-[#02b5ff]">{comment.coach_name}</span>
                          <span className="text-xs text-zinc-500">
                            {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-300">{comment.text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500 mb-3">No comments yet</p>
                )}

                {/* Add Comment (Coaches only) */}
                {isCoachOrAdmin && (
                  <div className="flex gap-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add feedback on this week's nutrition..."
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-16 resize-none"
                    />
                    <Button
                      onClick={addWeeklyComment}
                      disabled={!newComment.trim() || addingComment}
                      className="bg-[#02b5ff] hover:bg-[#0099dd] self-end"
                    >
                      {addingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <PieChart className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No meal data for this week</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
